/* ============================================================
   Hero behavior
   - Carousel: auto-advance + dots, swipe, pause on hover
   - Marquee: clone-based seamless loop (never innerHTML),
              duration derived from measured width + px/sec speed
   - Respects prefers-reduced-motion
   ------------------------------------------------------------
   STABILITY NOTES:
   • Never rewrite track.innerHTML — it recreates <img> nodes and
     forces a re-fetch, which is what caused logos to fade in/out.
   • Clones are APPENDED only; existing nodes are never removed.
   • Resize only appends more copies / retimes; no teardown.
   • No unconditional setTimeout rebuild.
   ============================================================ */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Carousel ---------------- */
  function initCarousel(carousel) {
    const slides = Array.from(carousel.querySelectorAll('.hsg-hero__slide'));
    const dotsEl = carousel.querySelector('[data-dots]');
    if (slides.length < 2 || !dotsEl) return;

    let i = 0;
    let timer = null;
    const intervalMs = (parseInt(carousel.dataset.interval, 10) || 6) * 1000;

    slides.forEach((_, idx) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hsg-hero__dot' + (idx === 0 ? ' is-active' : '');
      b.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
      b.addEventListener('click', () => go(idx, true));
      dotsEl.appendChild(b);
    });
    const dots = Array.from(dotsEl.children);

    function go(next, fromUser) {
      slides[i].classList.remove('is-active');
      dots[i].classList.remove('is-active');
      i = (next + slides.length) % slides.length;
      slides[i].classList.add('is-active');
      dots[i].classList.add('is-active');
      if (fromUser) restart();
    }

    function tick()    { go(i + 1); }
    function start()   { if (!reduceMotion && !timer) timer = setInterval(tick, intervalMs); }
    function stop()    { if (timer) { clearInterval(timer); timer = null; } }
    function restart() { stop(); start(); }

    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', () => {
      document.visibilityState === 'visible' ? restart() : stop();
    });

    let startX = null;
    carousel.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    carousel.addEventListener('touchend', (e) => {
      if (startX == null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) go(i + (dx < 0 ? 1 : -1), true);
      startX = null;
    });

    start();
  }

  /* ---------------- Marquee ---------------- */
  function initMarquee(marquee) {
    const track = marquee.querySelector('.hsg-hero__marquee-track');
    if (!track) return;

    const sets = () => Array.from(track.querySelectorAll('.hsg-hero__marquee-set'));
    if (!sets().length) return;

    const heroEl = marquee.closest('.hsg-hero');
    const speed =
      parseFloat(marquee.dataset.speed) ||
      parseFloat(getComputedStyle(heroEl).getPropertyValue('--hero-marquee-speed')) ||
      50;

    let lastWidth = 0;

    function setWidth() {
      const s = sets()[0];
      return s ? s.getBoundingClientRect().width : 0;
    }

    /* Append clones (never remove) until the track spans at least
       2x the container, keeping the set count EVEN so the -50%
       keyframe always lands on an identical frame. */
    function ensureCoverage() {
      const oneSet = setWidth();
      if (!oneSet) return false;

      const target = Math.max(marquee.clientWidth, window.innerWidth) * 2;
      let needed = Math.max(2, Math.ceil(target / oneSet));
      if (needed % 2 !== 0) needed += 1;

      const current = sets().length;
      if (needed > current) {
        const source = sets()[0];
        const frag = document.createDocumentFragment();
        for (let n = current; n < needed; n++) {
          const clone = source.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-marquee-set');
          clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
            el.removeAttribute('data-shopify-editor-block');
          });
          frag.appendChild(clone);
        }
        track.appendChild(frag);
      }
      return true;
    }

    function retime() {
      const total = track.getBoundingClientRect().width;
      if (!total) return;
      const duration = (total / 2) / speed;
      track.style.setProperty('--marquee-dur', duration.toFixed(3) + 's');
      marquee.setAttribute('data-marquee-ready', 'true');
      lastWidth = marquee.clientWidth;
    }

    function build() {
      if (ensureCoverage()) retime();
    }

    /* Wait for real decoded widths before measuring. */
    const imgs = Array.from(track.querySelectorAll('img'));
    if (!imgs.length) {
      requestAnimationFrame(build);
    } else {
      Promise.all(
        imgs.map((img) =>
          img.complete && img.naturalWidth > 0
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              })
        )
      ).then(() => requestAnimationFrame(build));
    }

    /* Resize: only act on a real horizontal change (ignores mobile
       URL-bar height jitter, which fires resize on scroll). */
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (Math.abs(marquee.clientWidth - lastWidth) < 50) return;
        build();
      }, 200);
    };

    if ('ResizeObserver' in window) {
      new ResizeObserver(onResize).observe(marquee);
    } else {
      window.addEventListener('resize', onResize);
    }
  }

  function init(root) {
    const scope = root || document;
    scope.querySelectorAll('.hsg-hero [data-carousel]').forEach(initCarousel);
    scope.querySelectorAll('.hsg-hero [data-marquee]').forEach(initMarquee);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.hsg-hero')) init(e.target);
  });
})();